import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Button, Form, Input, InputNumber, Typography } from 'antd';
import { MinusCircleOutlined } from '@ant-design/icons';

import useStyles from './style';

const MAX_TIERS_LENGTH = 6;

const TierInputControl = ({ visible, tenancyInstalments = [], onCancel, onConfirm }) => {
  const { t } = useTranslation(['landlord', 'cms']);
  const classes = useStyles();

  const [isRequired, setIsRequired] = useState(false);
  const [isShowRemoveWarning, setIsShowRemoveWarning] = useState(false);
  const [tenancyTiers, setTenancyTiers] = useState(tenancyInstalments);

  const handleInputNumberChange = (value, index) => {
    const val = typeof value === 'number' ? parseInt(value, 10) : '';

    setTenancyTiers(prevVal =>
      prevVal.map((item, idx) => ({
        ...item,
        toTenancy: index === idx ? val : item.toTenancy,
        fromTenancy: index === idx - 1 ? val : item.fromTenancy,
      })),
    );
  };

  const handleAddNewTierClick = () => {
    setTenancyTiers(prevVal =>
      prevVal.concat({
        fromTenancy: null,
        toTenancy: null,
      }),
    );
    setIsRequired(false);
  };

  const handleRemoveTierClick = idx => {
    setTenancyTiers(prevVal =>
      prevVal
        .filter((_, index) => index !== idx)
        .map((item, index, arr) => ({
          ...item,
          fromTenancy: index == 0 ? 0 : arr[index - 1].toTenancy,
        }))
        .map((item, index, arr) => ({
          ...item,
          toTenancy: index == arr.length - 1 ? null : arr[index + 1].fromTenancy,
        })),
    );
    setIsShowRemoveWarning(true);
  };

  const getFromTenancyText = toTenancy => {
    if (!toTenancy && toTenancy !== 0) {
      return null;
    } else if ([0, 1].includes(toTenancy)) {
      return toTenancy + ' week';
    } else {
      return '> ' + toTenancy + ' weeks';
    }
  };

  const getToTenancyValidateProps = (tier, index, required = isRequired) => {
    if (
      tier.toTenancy !== null &&
      tier.fromTenancy !== null &&
      tier.toTenancy <= tier.fromTenancy
    ) {
      return {
        validateStatus: 'error',
        help: t('landlord.payment.long_tail.rent_payment.setting_modal.to_tenancy.error', {
          number: tier.fromTenancy,
        }),
      };
    } else if (
      (index !== tenancyTiers.length - 1 && !tier.toTenancy && tier.toTenancy !== 0) ||
      (index !== 0 && !tier.fromTenancy && tier.fromTenancy !== 0)
    ) {
      if (required) {
        return {
          validateStatus: 'error',
          help: t('landlord.payment.long_tail.rent_payment.setting_modal.empty.err'),
        };
      } else {
        return {};
      }
    } else {
      return {};
    }
  };

  const handleCancelClick = () => {
    onCancel();
  };

  const handleConfirmClick = () => {
    setIsRequired(true);

    if (
      tenancyTiers.some(
        (tier, index) => Object.keys(getToTenancyValidateProps(tier, index, true)).length > 0,
      )
    ) {
      return;
    }

    onConfirm(tenancyTiers);
  };

  useEffect(() => {
    if (visible) {
      setIsShowRemoveWarning(false);
      setTenancyTiers(tenancyInstalments);
    }
  }, [visible]);

  return (
    <div className={classes.tierInputContainer}>
      <div className={classes.tierInputMain}>
        <Row className="tier--input__header">
          <Col span={10} push={2}>
            {t('landlord.payment.long_tail.rent_payment.setting_modal.from_tenancy')}
          </Col>
          <Col span={10} push={2}>
            {t('landlord.payment.long_tail.rent_payment.setting_modal.to_tenancy')}
          </Col>
        </Row>

        <For each="tier" index="index" of={tenancyTiers}>
          <Row className="tier--input__row" gutter={10} type="flex">
            <Col span={2} className="tier--input__index">
              {t('landlord.payment.long_tail.rent_payment.setting_modal.tier') + ' ' + (index + 1)}
            </Col>
            <Col span={20}>
              <Form.Item {...getToTenancyValidateProps(tier, index)}>
                <Row gutter={10}>
                  <Col span={12}>
                    <Input disabled={true} value={getFromTenancyText(tier.fromTenancy)} />
                  </Col>
                  <Col span={12} style={{ position: 'relative' }}>
                    <Choose>
                      <When condition={index === tenancyTiers.length - 1}>
                        <Input disabled={true} value={'-'} />
                      </When>
                      <Otherwise>
                        <InputNumber
                          className="tier--input__to-tenancy"
                          step={1}
                          max={99999}
                          value={tier.toTenancy}
                          onChange={val => handleInputNumberChange(val, index)}
                        />

                        <span className="tier--input__to-tenancy-week">
                          {t(
                            tier.toTenancy > 1
                              ? 'landlord.payment.long_tail.rent_payment.setting_modal.weeks'
                              : 'landlord.payment.long_tail.rent_payment.setting_modal.week',
                          )}
                        </span>
                      </Otherwise>
                    </Choose>
                  </Col>
                </Row>
              </Form.Item>
            </Col>
            <Col span={2}>
              <If condition={tenancyTiers.length > 2}>
                <MinusCircleOutlined
                  className="tier--input__close"
                  onClick={() => handleRemoveTierClick(index)}
                />
              </If>
            </Col>
          </Row>
        </For>

        <Row>
          <If condition={isShowRemoveWarning}>
            <Col span={22} push={2}>
              <Typography.Paragraph type="warning">
                {t('landlord.payment.long_tail.rent_payment.setting_modal.remove_warning')}
              </Typography.Paragraph>
            </Col>
          </If>
          <If condition={tenancyTiers.length !== MAX_TIERS_LENGTH}>
            <Col span={22} push={2}>
              <Button type="link" className="tier--button__add" onClick={handleAddNewTierClick}>
                {t('landlord.payment.long_tail.rent_payment.setting_modal.add_new_tier')}
              </Button>
            </Col>
          </If>
        </Row>
      </div>
      <div className={classes.tierInputFooter}>
        <span className="tier-input--total">
          {t('landlord.payment.long_tail.rent_payment.setting_modal.total_tier', {
            count: tenancyTiers.length,
          })}
        </span>
        <div className="tier-input--button">
          <Button onClick={handleCancelClick}>{t('cancel')}</Button>
          <Button
            type="primary"
            className="tier-input--button__confirm"
            onClick={handleConfirmClick}
          >
            {t('confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TierInputControl;
